import { apiClient } from '../client';

/**
 * The dialer's Performances page (W4): every figure is computed on the server
 * over the whole period (POST /api/analytics/dialer/*, docs/api/CHANGELOG.md
 * 0.4.0). « Connecté » = the call reached the agent (agent_connected_at).
 */

export type Grain = 'hour' | 'day' | 'week';

export interface DialerFilters {
  from?: string;
  to?: string;
  agent_email?: string;
  list?: string;
  customer_id?: string;
  timezone?: string;
}

export interface DialerCounters {
  total: number;
  connected: number;
  conversations: number;
  meetings: number;
  callbacks: number;
  callbacksConnected: number;
  voicemails: number;
  noAnswer: number;
  notInterested: number;
  iosFilters: number;
  cancelled: number;
  smsSent: number;
  shortConnected: number;
  talkSeconds: number;
  durationSeconds: number;
  durationCount: number;
  activeDays: number;
  firstCallAt: string | null;
  lastCallAt: string | null;
}

export interface DialerPerformance {
  totals: DialerCounters;
  previous: DialerCounters | null;
  byOutcome: Record<string, number>;
  series: Array<{ key: string; total: number; connected: number; conversations: number; meetings: number }>;
  /** agentEmail is absent for a viewer (end client): names only (REV-W4-S9). */
  byAgent: Array<DialerCounters & { agentEmail?: string; agentName: string }>;
  byPhonePrefix: Array<{ prefix: string; total: number; connected: number; meetings: number }>;
}

export interface HeatmapCell { day: number; hour: number; total: number; connected: number; meetings: number }

export interface CallerNumberStat {
  number: string;
  total: number;
  answered: number;
  connected: number;
  meetings: number;
  answerRate: number | null;
  connectRate: number | null;
  lastCallAt: string | null;
}

export interface ListCompletionRow {
  list: string;
  total: number;
  called: number;
  remaining: number;
  disabled: number;
  exhausted: number | null;
  connectedContacts: number;
  calls: number;
  meetings: number;
  completion: number | null;
  lastCallAt: string | null;
}

export interface QualificationAccuracy {
  total: number;
  accepted: number;
  acceptanceRate: number | null;
  outcomes: Array<{
    suggested: string;
    total: number;
    accepted: number;
    changed: number;
    acceptanceRate: number | null;
    changedTo: Array<{ outcome: string; count: number }>;
  }>;
}

export function getDialerPerformance(body: DialerFilters & { conversation_threshold?: number; grain?: Grain; compare_from?: string; compare_to?: string }, signal?: AbortSignal) {
  return apiClient.post<DialerPerformance>('/analytics/dialer/performance', body, { signal });
}

export function getDialerHeatmap(body: DialerFilters, signal?: AbortSignal) {
  return apiClient.post<{ timezone: string; cells: HeatmapCell[] }>('/analytics/dialer/heatmap', body, { signal });
}

export function getDialerNumbers(body: DialerFilters, signal?: AbortSignal) {
  return apiClient.post<{ numbers: CallerNumberStat[] }>('/analytics/dialer/numbers', body, { signal });
}

export function getListCompletion(body: { agent_email?: string; list?: string; customer_id?: string; max_attempts?: number }, signal?: AbortSignal) {
  return apiClient.post<{ lists: ListCompletionRow[] }>('/analytics/dialer/list-completion', body, { signal });
}

export function getQualificationAccuracy(body: { from?: string; to?: string; customer_id?: string }, signal?: AbortSignal) {
  return apiClient.post<QualificationAccuracy>('/analytics/dialer/qualification-accuracy', body, { signal });
}

/** One page of calls (W2 paged route), for the drill-down and the CSV export. */
export function getCallsPage(body: { filter: Record<string, unknown>; limit?: number; cursor?: string }, signal?: AbortSignal) {
  return apiClient.post<{ items: Array<Record<string, unknown>>; next_cursor: string | null }>('/entities/ParallelCall/page', { sort: '-created_date', ...body }, { signal });
}

/** Every phone of a list (ProspectQueue.source), page by page: no cap. */
export async function fetchListPhones(list: string, customerId?: string, signal?: AbortSignal): Promise<string[]> {
  const phones = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = await apiClient.post<{ items: Array<{ prospect_phone?: string }>; next_cursor: string | null }>(
      '/entities/ProspectQueue/page',
      { filter: { source: list, ...(customerId ? { customer_id: customerId } : {}) }, limit: 200, ...(cursor ? { cursor } : {}) },
      { signal },
    );
    for (const p of page.items) if (p.prospect_phone) phones.add(p.prospect_phone);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return [...phones];
}

/** Every call matching a filter, page by page (the CSV export): no cap. */
export async function fetchAllCalls(filter: Record<string, unknown>, signal?: AbortSignal): Promise<Array<Record<string, unknown>>> {
  const out: Array<Record<string, unknown>> = [];
  let cursor: string | undefined;
  do {
    const page = await getCallsPage({ filter, limit: 200, ...(cursor ? { cursor } : {}) }, signal);
    out.push(...page.items);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return out;
}
